import { FC, Suspense, useEffect, useState } from 'react'
import { Box, useMediaQuery, SimpleGrid } from '@chakra-ui/react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { AppRoute, routes } from '../pages/routes'
import { useAppDispatch, useAppSelector } from './typedReduxHooks'
import { DESKTOP_BREAKPOINT } from '../constants'
import HeaderBar from '../components/shared-components/HeaderBar'
import Nav from '../components/shared-components/SideNav'
import IdleTimer from '../utils/idle-timer'
import UserAgreementPage from '../pages/UserAgreementPage'
import { appDidFinishLaunching } from './appSlice'
import { useLogout } from '../hooks/useLogout'
import './styles/App.css'
import { useSSOClientConfig, ssoStorage, SSO_PROVIDER_PLATFORM } from '../features/sso'
import {
  isBootstrapRedirectPending,
  resolveBootstrapRedirect,
} from '../features/authentication/bootstrapRedirect'
import { Permission } from '../types/permission'
import MaintenanceBanner from '../components/MaintenanceBanner'
import { InitLocationModal } from '../features/locations/components/InitLocationModal'
import { useHasEntitlement } from '../pages/OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../types/organization-entitlements'
import { isEhrAuthSession } from '../features/ehr-launch/ehrLaunchStorage'
import { AuthenticatedContextGate } from '../features/authenticated-context/AuthenticatedContextGate'
import { loadAuthenticatedContext } from '../features/authenticated-context/authenticatedContextSlice'
import { ehrLaunchCoordination } from '../features/ehr-launch/ehrLaunchCoordination'
import {
  expireEhrSession,
  observeEhrLaunch,
  verifyActiveEhrLaunch,
} from '../features/ehr-launch/ehrPatientAcquisitionSlice'
import { subscribeToEhrSessionInvalidation } from '../features/ehr-launch/ehrSessionEvents'
import { trackAuthLandingExit } from '../common/utils/analytics'

const App = () => {
  const dispatch = useAppDispatch()
  const idleTimeout = useAppSelector((state) => state.organization?.data?.settings?.idleTimeout)
  const authenticatedProvider = useAppSelector((state) => state.authenticatedProvider)
  const logout = useLogout()

  // Where this load has to go, given what the last redirect left behind. The
  // decision is in resolveBootstrapRedirect, which is where its branches are
  // tested.
  //
  // Resolved during the first render rather than in an effect. Cognito's
  // /logout can only return to a sign-out URL registered on the app client, so
  // a sign-out lands on /login -- and an effect runs after the first paint,
  // which was long enough to mount LoginPage and show it before forwarding.
  // The initializer runs once, and both hand-offs it reads are read-and-clear.
  // Read-only, so re-running this render can never swallow a hand-off: it only
  // asks whether there is one. The consuming read is in the effect below.
  const [hasPendingBootstrap] = useState(() =>
    isBootstrapRedirectPending(
      window.location.pathname,
      Boolean(localStorage.getItem('access_token'))
    )
  )
  const [bootstrapRedirect, setBootstrapRedirect] = useState<string | null>(null)
  const [isBootstrapResolved, setIsBootstrapResolved] = useState(!hasPendingBootstrap)

  // Consumes the hand-offs, which is a write, so it belongs in an effect rather
  // than in a render React is free to discard and repeat.
  useEffect(() => {
    if (isBootstrapResolved) return
    setBootstrapRedirect(
      resolveBootstrapRedirect(
        window.location.pathname,
        Boolean(localStorage.getItem('access_token'))
      )
    )
    setIsBootstrapResolved(true)
  }, [])

  // Dropped as soon as it has been acted on, so the forward happens exactly
  // once. Keeping it set and comparing against the current location instead
  // re-fires on every later navigation -- an SSO landing that bails to /login
  // then bounces straight back, forever. Child effects run before this one, so
  // Navigate has already moved by the time this clears it.
  useEffect(() => {
    if (bootstrapRedirect) setBootstrapRedirect(null)
  }, [bootstrapRedirect])

  // Runs once on mount — handles page refresh initialization only
  useEffect(() => {
    if (!localStorage.getItem('access_token')) return
    dispatch(appDidFinishLaunching())
  }, [])

  useEffect(() => {
    if (authenticatedProvider !== 'ehr') return
    const unsubscribeLaunch = ehrLaunchCoordination.subscribe((launchInstanceId) => {
      dispatch(observeEhrLaunch(launchInstanceId))
    })
    const unsubscribeFocus = ehrLaunchCoordination.subscribeToFocus(() => {
      void dispatch(verifyActiveEhrLaunch())
    })
    const unsubscribeInvalidation = subscribeToEhrSessionInvalidation(() => {
      dispatch(expireEhrSession())
    })
    return () => {
      unsubscribeLaunch()
      unsubscribeFocus()
      unsubscribeInvalidation()
    }
  }, [authenticatedProvider, dispatch])

  // Runs when idleTimeout changes — manages the idle timer only
  useEffect(() => {
    const timer = new IdleTimer({
      onTimeout: () => {
        const isEhrSession = isEhrAuthSession()
        const hasAccessToken = !!localStorage.getItem('access_token')
        const hasCognitoSessionMaterial =
          hasAccessToken &&
          !!localStorage.getItem('refresh_token') &&
          !!localStorage.getItem('client_id')

        if (isEhrSession && !hasAccessToken) {
          return
        }

        if (
          !isEhrSession &&
          !hasAccessToken &&
          !localStorage.getItem('refresh_token') &&
          !localStorage.getItem('client_id')
        ) {
          return
        }
        const ssoContext = ssoStorage.getContext()
        const isCognitoSSO = ssoContext?.providerPlatform === SSO_PROVIDER_PLATFORM.COGNITO
        logout({ skipCognitoLogout: isEhrSession || isCognitoSSO || !hasCognitoSessionMaterial })
      },
      timeoutInMs:
        (idleTimeout && idleTimeout <= 5400 && idleTimeout >= 300 ? idleTimeout : 900) * 1000, // default value: 15 mins
    })
    return () => timer.cleanUpTracker()
  }, [idleTimeout, logout])

  // Both branches render nothing, so the route this load happens to have landed
  // on never paints on its way past.
  if (!isBootstrapResolved) return null
  if (bootstrapRedirect) return <Navigate to={bootstrapRedirect} replace />

  return (
    <>
      <MaintenanceBanner />
      <Suspense fallback={null}>
        <Routes>
          {Object.values(routes).map(({ path, Component, requiresAuth }) => (
            <Route
              path={path}
              element={
                requiresAuth ? <AuthComponent path={path} Component={Component} /> : <Component />
              }
              key={path}
            />
          ))}
        </Routes>
      </Suspense>
    </>
  )
}

const routesWithoutNav = [
  routes.acceptUserInvitation.path,
  routes.createPrescriberProfile.path,
  routes.createOrganizationAdminProfile.path,
  routes.selectRoles.path,
  routes.createClinic.path,
  routes.linkOrganization.path,
  routes.pricePlan.path,
  routes.error.path,
  routes.root.path,
  routes.organizationJoinResponse.path,
  routes.createOrganization.path,
  routes.joinOrganizationPending.path,
  routes.setDefaultPaymentMethodGuide.path,
]

const routesWithoutUserProfile = [
  routes.acceptUserInvitation.path,
  routes.createPrescriberProfile.path,
  routes.createOrganizationAdminProfile.path,
  routes.createClinic.path,
  routes.linkOrganization.path,
  routes.selectRoles.path,
  routes.organizationJoinResponse.path,
  routes.createOrganization.path,
  routes.joinOrganizationPending.path,
  routes.approveUserInvitation.path,
  routes.approveJoinRequest.path,
]

const routesWithoutAuthenticatedContext = routesWithoutUserProfile

const routesShouldRedirectAfterLogin = [
  routes.approveUserInvitation.path,
  routes.approveJoinRequest.path,
  routes.settings.path,
]

const routesShouldGoToMainPage = [
  routes.acceptUserInvitation.path,
  routes.createPrescriberProfile.path,
  routes.createOrganizationAdminProfile.path,
  routes.selectRoles.path,
  routes.createClinic.path,
  routes.linkOrganization.path,
  routes.login.path,
  routes.root.path,
  routes.organizationJoinResponse.path,
  routes.createOrganization.path,
  routes.joinOrganizationPending.path,
]

const AuthComponent: FC<AppRoute> = ({ Component, path }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const logout = useLogout()

  const [isSmallScreenNavOpen, setIsSmallScreenNavOpen] = useState(false)
  const [isDesktop] = useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT})`)
  const {
    isPrescriberOnly: isClientOnlyCreatePrescriber,
    hasFixedOrganization: isClientHasFixedOrganization,
    requiresJoinRequestApproval,
  } = useSSOClientConfig()
  const usesDirectFixedOrgOnboarding = isClientHasFixedOrganization && !requiresJoinRequestApproval
  const authentication = useAppSelector((state) => state.authentication)
  const contextStatus = useAppSelector((state) => state.authenticatedContext.status)
  const hasMultipleLocations = useHasEntitlement(FeatureKey.MULTIPLE_LOCATIONS)
  const isPrescriberActive = useAppSelector((state) => state.prescriber.prescriber?.active)

  useEffect(() => {
    if (
      authentication.loginFinished &&
      authentication.hasUserProfile &&
      contextStatus === 'idle' &&
      !routesWithoutAuthenticatedContext.includes(location.pathname)
    ) {
      dispatch(loadAuthenticatedContext())
    }
  }, [authentication, contextStatus, dispatch, location.pathname])

  useEffect(() => {
    if (!authentication?.userId) {
      if (
        routesShouldRedirectAfterLogin.includes(location.pathname) &&
        location.search.includes('external')
      ) {
        const url = new URL(window.location.href)
        url.searchParams.delete('external')
        ssoStorage.setRedirectUrl(url.pathname + url.search)
      }
      // Recorded because AuthenticationPage's own auth_landing_exit stops at
      // "recovered into the app". Without this, a session that still holds
      // tokens but has no userId gets bounced on to /login - where Sign In
      // clears localStorage - and the telemetry never shows it.
      trackAuthLandingExit(
        'no_user_id',
        routes.login.path,
        Boolean(localStorage.getItem('access_token'))
      )
      navigate(routes.login.path)
      return
    }
  }, [navigate, authentication])
  useEffect(() => {
    if (authentication?.hasError) {
      trackAuthLandingExit(
        'profile_load_error',
        routes.error.path,
        Boolean(localStorage.getItem('access_token'))
      )
      navigate(routes.error.path)
      return
    }
  }, [authentication, navigate])
  useEffect(() => {
    if (!authentication?.userId) return
    if (authentication?.hasError) return
    if (authentication?.loginFinished && !authentication?.hasUserProfile) {
      if (!routesWithoutUserProfile.includes(location.pathname)) {
        if (usesDirectFixedOrgOnboarding) {
          if (isClientOnlyCreatePrescriber) {
            navigate(routes.createPrescriberProfile.path)
          } else {
            navigate(routes.selectRoles.path)
          }
        } else {
          navigate(routes.joinOrganizationPending.path)
        }
      }
      return
    }
  }, [
    location.pathname,
    navigate,
    authentication,
    usesDirectFixedOrgOnboarding,
    isClientOnlyCreatePrescriber,
  ])

  useEffect(() => {
    if (!authentication?.userId || authentication?.hasError) return
    const checkAuthentication = async () => {
      const isEhrSession = isEhrAuthSession()
      const hasAccessToken = !!localStorage.getItem('access_token')
      const hasRequiredSessionMaterial = isEhrSession
        ? hasAccessToken
        : hasAccessToken &&
          !!localStorage.getItem('refresh_token') &&
          !!localStorage.getItem('client_id')

      if (!hasRequiredSessionMaterial) {
        await logout()
        return
      }
      if (
        authentication.hasUserProfile &&
        (routesWithoutAuthenticatedContext.includes(location.pathname) ||
          contextStatus === 'ready') &&
        routesShouldGoToMainPage.includes(location.pathname)
      ) {
        if (authentication?.permissions?.includes(Permission.PRESCRIBING)) {
          navigate(routes.newScript.path)
          return
        } else {
          navigate(routes.settings.path)
          return
        }
      }
    }
    checkAuthentication()
  }, [authentication, contextStatus, dispatch, location.pathname, logout, navigate])

  if (!authentication?.userId) {
    return null
  }

  const authenticatedPage = routesWithoutNav.includes(path) ? (
    <Component />
  ) : (
    <>
      <Box>
        {!isDesktop && (
          <HeaderBar onToggleNav={() => setIsSmallScreenNavOpen(!isSmallScreenNavOpen)} />
        )}
        <SimpleGrid
          height={isDesktop ? '100vh' : 'calc(100vh - 67px)'}
          alignItems="stretch"
          bgColor="blackAlpha.100"
          columns={2}
          gridTemplateColumns={isDesktop ? 'auto 1fr' : '1fr'}
        >
          <Nav
            isSmallScreenNavOpen={isSmallScreenNavOpen}
            onCloseSmallScreenNav={() => setIsSmallScreenNavOpen(false)}
          />
          <Component />
        </SimpleGrid>
      </Box>
      <UserAgreementPage />
      {hasMultipleLocations && authentication.loginFinished && isPrescriberActive && (
        <InitLocationModal />
      )}
    </>
  )

  if (routesWithoutAuthenticatedContext.includes(path)) return authenticatedPage

  return <AuthenticatedContextGate>{authenticatedPage}</AuthenticatedContextGate>
}

export default App
